import { log } from "src/handlers/log";

// Phase I2: let the UI react to an expired session. The event is fired at
// most once every few seconds so a burst of simultaneous 401s from parallel
// writes doesn't open N re-auth dialogs.
let lastAuthExpiredDispatchMs = 0;
const AUTH_EXPIRED_COOLDOWN_MS = 5_000;

function dispatchAuthExpired(context) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastAuthExpiredDispatchMs < AUTH_EXPIRED_COOLDOWN_MS) return;
  lastAuthExpiredDispatchMs = now;
  try {
    window.dispatchEvent(
      new CustomEvent("netbase:authExpired", { detail: { context } })
    );
  } catch {
    // Ignore — the dispatch is advisory; we still rethrow the error.
  }
}

// ---------------------------------------------------------------------------
// Sync-write fallback policy.
//
// When a direct `tablesDB.updateRow` / `createRow` fails, the caller has to
// decide whether to:
//   - rethrow (surface the error to the UI),
//   - retry,
//   - or route through the `project-sync` Cloud Function which runs with
//     admin credentials and re-derives ACLs.
//
// The original implementation collapsed 404/401/403 into a single
// "everything goes through the function" path. That made permission glitches
// silently expensive (every failed write triggered an admin invocation). The
// new policy is:
//
//   - 404 \u2014 row missing. The caller should `createRow` directly, no need
//     to involve the function.
//   - 401 \u2014 unauthenticated. We can't fix this client-side; surface so the
//     UI can re-authenticate. Falls back to legacy if the killswitch is on.
//   - 403 \u2014 forbidden. ACLs are stale; routing through the admin function
//     is the right call (it re-derives permissions for the project Team).
//   - other \u2014 rethrow.
//
// Killswitch: setting `localStorage["netbase:syncFallback"] = "legacy"`
// reverts to the old "any 4xx \u2192 function" behaviour for one release window
// in case the new policy regresses something we didn't anticipate.
// ---------------------------------------------------------------------------

const KILLSWITCH_KEY = "netbase:syncFallback";

function legacyKillswitchActive() {
  try {
    return localStorage.getItem(KILLSWITCH_KEY) === "legacy";
  } catch {
    return false;
  }
}

/**
 * Classify the error from a direct write attempt and tell the caller what to
 * do. Possible decisions:
 *   - { action: "throw", error }            \u2014 rethrow as-is
 *   - { action: "create" }                  \u2014 row missing (404), retry as create
 *   - { action: "fallback", reason: "..." } \u2014 route through the cloud function
 *
 * @param {{ context: string, code: number, error: any, deviceType?: string,
 *           tabId?: number, page?: string }} input
 */
export function classifyWriteError(input) {
  const { context, code, error } = input;

  if (legacyKillswitchActive()) {
    if (code === 404 || code === 401 || code === 403) {
      log.warn("sync.fallback.legacy", {
        context,
        code,
        deviceType: input.deviceType,
        tabId: input.tabId,
        page: input.page
      });
      return { action: "fallback", reason: "killswitch" };
    }
    return { action: "throw", error };
  }

  if (code === 404) {
    log.info("sync.fallback.create", {
      context,
      deviceType: input.deviceType,
      tabId: input.tabId,
      page: input.page
    });
    return { action: "create" };
  }

  if (code === 403) {
    log.warn("sync.fallback.permission", {
      context,
      code,
      deviceType: input.deviceType,
      tabId: input.tabId,
      page: input.page
    });
    return { action: "fallback", reason: "forbidden" };
  }

  if (code === 401) {
    log.warn("sync.unauthenticated", {
      context,
      deviceType: input.deviceType,
      tabId: input.tabId,
      page: input.page
    });
    dispatchAuthExpired(context);
    return { action: "throw", error };
  }

  return { action: "throw", error };
}

// Internal exports for tests.
export const __test__ = {
  KILLSWITCH_KEY,
  legacyKillswitchActive,
  resetAuthExpiredCooldown: () => {
    lastAuthExpiredDispatchMs = 0;
  }
};
